import { PlanType } from "@brookbush/database";

import clsx from "clsx";
import type Stripe from "stripe";

import Skeleton from "~/components/Skeleton";
import { useStripeProration } from "~/hooks";
import useCheckout from "~/hooks/useCheckout";
import useJoiningPrice from "~/hooks/useJoiningPrice";
import usePlanConfig from "~/hooks/usePlanConfig";
import useStripePrices from "~/hooks/useStripePrices";
import {
	getSelectedPlanJoiningCopy,
	getTrialEndsLabel,
} from "~/lib/membership-joining-price";
import {
	convertToDollars,
	formatPrice,
	getAmountIndividual,
	getAmountIndividualAfterDiscount,
	getAmountPerSeat,
	getAmountPerSeatAfterDiscount,
	getBillingInterval,
	getFirstBillingDate,
	getTotalAmountDue,
} from "~/lib/stripe/helpers";

const BecomeAMemberCheckoutSummary = ({ isModal }: { isModal?: boolean }) => {
	const { planConfig, isLoadingPlanConfig } = usePlanConfig();
	const {
		flags: { isChangingPlan },
	} = useCheckout();
	const { proration, isLoadingProration } = useStripeProration(
		isChangingPlan && !isLoadingPlanConfig ? planConfig : undefined,
	);
	const { prices } = useStripePrices(planConfig.planType);
	const { offer, ready: isJoiningPriceReady } = useJoiningPrice();

	if (!planConfig.price) return null;

	const joiningCopy = isChangingPlan
		? null
		: getSelectedPlanJoiningCopy({
				planConfig,
				prices,
				offer,
				trialEndsLabel: getTrialEndsLabel(planConfig.trialLength),
			});

	const items = [
		!isModal && {
			name: "Plan Type",
			value: planConfig.planType.toLowerCase(),
		},

		planConfig.promoCode && {
			name: "Discount",
			value: (
				<span className="text-emerald-600 dark:text-emerald-300">
					{planConfig.promoCode.coupon.percent_off
						? `${planConfig.promoCode.coupon.percent_off}% Off For 1 Year`
						: `$${convertToDollars(
								planConfig.promoCode.coupon.amount_off!,
							)} Off For 1 Year`}
				</span>
			),
		},

		!isModal && {
			name:
				planConfig.planType === PlanType.INDIVIDUAL
					? "Price"
					: "Price Per User",
			value:
				planConfig.planType === PlanType.INDIVIDUAL ? (
					planConfig.promoCode ? (
						<>
							<span className="text-emerald-600 dark:text-emerald-300">
								{formatPrice({
									currency: planConfig.currency,
									value: getAmountIndividualAfterDiscount(
										planConfig.price,
										planConfig.promoCode.coupon,
									),
								})}
							</span>{" "}
							<span className="line-through">
								{formatPrice({
									currency: planConfig.currency,
									value: getAmountIndividual(planConfig.price),
								})}
							</span>{" "}
							/ {getBillingInterval(planConfig.price)}
						</>
					) : (
						`${formatPrice({
							value: getAmountIndividual(planConfig.price),
							currency: planConfig.currency,
						})} / ${getBillingInterval(planConfig.price)}`
					)
				) : planConfig.promoCode ? (
					<>
						<span className="text-emerald-600 dark:text-emerald-300">
							$
							{getAmountPerSeatAfterDiscount(
								planConfig.price,
								planConfig.quantity,
								planConfig.promoCode.coupon,
							)}
						</span>{" "}
						<span className="line-through">
							${getAmountPerSeat(planConfig.price, planConfig.quantity)}
						</span>{" "}
						/ {getBillingInterval(planConfig.price)}
					</>
				) : (
					`$${getAmountPerSeat(
						planConfig.price,
						planConfig.quantity,
					)} / ${getBillingInterval(planConfig.price)}`
				),
		},

		planConfig.planType === PlanType.TEAM && {
			name: "Number Of Users",
			value: planConfig.quantity,
		},

		planConfig.planType === PlanType.TEAM && {
			name: "Total Price",
			value: planConfig.promoCode ? (
				<>
					<span className="text-emerald-600 dark:text-emerald-300">
						{formatPrice({
							value:
								getAmountPerSeatAfterDiscount(
									planConfig.price,
									planConfig.quantity,
									planConfig.promoCode.coupon,
								) * planConfig.quantity,
							currency: planConfig.currency,
						})}
					</span>{" "}
					<span className="line-through">
						{formatPrice({
							currency: planConfig.currency,
							value:
								getAmountPerSeat(planConfig.price, planConfig.quantity) *
								planConfig.quantity,
						})}

						{
							+(
								getAmountPerSeat(planConfig.price, planConfig.quantity) *
								planConfig.quantity
							).toFixed(2)
						}
					</span>{" "}
					/ {getBillingInterval(planConfig.price)}
				</>
			) : (
				`${formatPrice({
					currency: planConfig.currency,
					value:
						getAmountPerSeat(planConfig.price, planConfig.quantity) *
						planConfig.quantity,
				})} / ${getBillingInterval(planConfig.price)}`
			),
		},

		planConfig.trialLength && {
			name: "Trial Period",
			value: `1 ${planConfig.trialLength}`,
		},

		planConfig.trialLength && {
			name: "First Billing Date",
			value: getFirstBillingDate(planConfig.trialLength),
		},

		...(proration?.lines?.data.length
			? proration.lines.data.map((lineItem) => ({
					name: lineItem.description ?? "Adjustment",
					value: `${formatPrice({
						currency: planConfig.currency,
						value: +(lineItem.amount / 100).toFixed(2),
					})}`,
				}))
			: []),
	].filter(Boolean) as { name: string; value: string }[];

	const prorationMatchesSelection = proration?.lines.data.some((line) => {
		const price = (
			line as Stripe.InvoiceLineItem & {
				price?: { id?: string } | string | null;
			}
		).price;
		const priceId = typeof price === "string" ? price : price?.id;
		return priceId === planConfig.price?.id;
	});

	const selectedAmountDue = getTotalAmountDue(planConfig);
	const postTrialAmountDue =
		planConfig.planType === PlanType.INDIVIDUAL
			? planConfig.promoCode
				? getAmountIndividualAfterDiscount(
						planConfig.price,
						planConfig.promoCode.coupon,
					)
				: getAmountIndividual(planConfig.price)
			: planConfig.promoCode
				? getAmountPerSeatAfterDiscount(
						planConfig.price,
						planConfig.quantity,
						planConfig.promoCode.coupon,
					)! * planConfig.quantity
				: +(
						getAmountPerSeat(planConfig.price, planConfig.quantity)! *
						planConfig.quantity
					).toFixed(2);

	const totalAmountDue = !isLoadingPlanConfig
		? isChangingPlan && prorationMatchesSelection && !isLoadingProration
			? convertToDollars(proration!.total)
			: selectedAmountDue
		: undefined;

	const isCredited = totalAmountDue! <= 0;

	return (
		<div
			className={clsx("flex max-h-max flex-col gap-3", {
				"flex-1 basis-1/2 rounded-xl border px-4 py-3 shadow shadow-slate-100 lg:gap-5 lg:px-5 lg:py-4 dark:shadow-slate-900":
					!isModal,
			})}
		>
			{!!items.length && (
				<div className="flex flex-col gap-3 lg:gap-4">
					{items.map((item, index) => (
						<div
							className="flex justify-between gap-3 font-medium text-slate-700"
							key={`${item.name}-${index}`}
						>
							<p className="max-w-64">{item.name}: </p>
							<p className="shrink-0 whitespace-nowrap capitalize tabular-nums">
								{item.value}
							</p>
						</div>
					))}
				</div>
			)}

			{joiningCopy ? (
				(!isJoiningPriceReady || joiningCopy.checkout) && (
					<div
						className={clsx("font-medium lg:text-lg", {
							"mt-auto border-t pt-3 lg:pt-4 dark:border-slate-600": !isModal,
						})}
					>
						{isJoiningPriceReady ? (
							<p data-testid="joining-price-checkout">{joiningCopy.checkout}</p>
						) : (
							<Skeleton className="h-12 w-full" />
						)}
					</div>
				)
			) : (
				<div
					className={clsx("flex flex-col gap-1 font-medium lg:text-lg", {
						"mt-auto border-t pt-3 lg:pt-4 dark:border-slate-600": !isModal,
					})}
				>
					<div className="flex justify-between">
						{planConfig.trialLength ? (
							<p>Amount Due Today:</p>
						) : (
							<p>Amount Due: </p>
						)}

						<p className="shrink-0 whitespace-nowrap font-medium text-emerald-600 tabular-nums dark:text-emerald-300">
							{isCredited
								? `${formatPrice({
										value: 0,
										currency: planConfig.currency,
									})} (${formatPrice({
										value: Math.abs(totalAmountDue!),
										currency: planConfig.currency,
									})} Credit)`
								: formatPrice({
										value: totalAmountDue!,
										currency: planConfig.currency,
									})}
						</p>
					</div>

					{planConfig.trialLength && planConfig.price && (
						<p className="text-right font-normal text-slate-500 text-sm dark:text-slate-400">
							Then{" "}
							{formatPrice({
								value: postTrialAmountDue,
								currency: planConfig.currency,
							})}{" "}
							on {getFirstBillingDate(planConfig.trialLength)}
						</p>
					)}
				</div>
			)}
		</div>
	);
};

export default BecomeAMemberCheckoutSummary;
